var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var FormulaConfig_1;
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchema } from '@symon-ai/wisepipe-serialize';
import '@symon-ai/wisepipe-base-pipe';
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { FormulaValidationError, FormulaValidationInfo } from './formulaValidationInfo';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationState } from './validation/toolValidationResult';
import { isSourceValid } from './validation/validateHelper';
const schema = {
    $id: 'urn:wisepipe-common-pipe:FormulaConfig',
    type: 'object',
    properties: {
        column: {
            anyOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:StringToolVariable'
                },
                { type: 'string', minLength: 1 }
            ]
        },
        formula: {
            anyOf: [{ $ref: 'urn:wisepipe-base-pipe:CompoundToolVariable' }, { type: 'string', minLength: 1 }]
        }
    },
    required: ['column', 'formula']
};
// COPIED FROM FORMULAHELPER
export const FUNCTION_NAMES = [
    'round',
    'mround',
    'floor',
    'ceil',
    'abs',
    'sqrt',
    'log10',
    'log2',
    'sin',
    'cos',
    'tan',
    'asin',
    'acos',
    'atan',
    'pow',
    'if',
    'startswith',
    'endswith',
    'contains',
    'isnull',
    'concat',
    'date',
    'now',
    'add',
    'subtract',
    'multiply',
    'divide',
    'power',
    'square',
    'cube',
    'min',
    'max',
    'pad',
    'calculate',
    'total'
];
let FormulaConfig = FormulaConfig_1 = class FormulaConfig extends ToolConfig {
    constructor(column, formula, customFormats) {
        super(ToolType.FORMULA, customFormats);
        this.column = column;
        this.formula = formula;
        JSON.validate(this, FormulaConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return FormulaConfig_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return FormulaConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return FormulaConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return FormulaConfig_1.outs;
    }
    static get in() {
        return FormulaConfig_1.ins[0];
    }
    static get out() {
        return FormulaConfig_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.FORMULA.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.CALCULATE], this.ins, this.outs, FUNCTION_NAMES.map((name) => ({
            name,
            weight: name === 'date' ? 0.1 : undefined
        })), true);
    }
    isValidForCalc(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source) {
            return ToolValidationState.BAD_METADATA;
        }
        if (!isSourceValid(source, new Set(), [ToolConfig.getStringValue(iConfigHelper.getParameters(), this.column)])) {
            return ToolValidationState.BAD_METADATA;
        }
        return iConfigHelper.parseFormula(ToolConfig.getStringValue(iConfigHelper.getParameters(), this.formula), source)
            ? ToolValidationState.VALID
            : ToolValidationState.BAD_METADATA;
    }
    applyParameters(parameters) {
        return new FormulaConfig_1(ToolConfig.getStringValue(parameters, this.column), ToolConfig.getStringValue(parameters, this.formula));
    }
    isValidForCalcDetail(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source) {
            return new FormulaValidationInfo(ToolValidationState.BAD_METADATA, 'The syntax has an error. Check for incorrect placement of functions, operators and constraints.', FormulaValidationError.SYNTAX_ERROR);
        }
        if (!isSourceValid(source, new Set(), [ToolConfig.getStringValue(iConfigHelper.getParameters(), this.column)])) {
            return new FormulaValidationInfo(ToolValidationState.BAD_METADATA, 'The source is invalid', FormulaValidationError.INVALID_SOURCE);
        }
        return iConfigHelper.parseFormulaDetail(ToolConfig.getStringValue(iConfigHelper.getParameters(), this.formula), source);
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(this.in);
        const exp = tsb.export(this.out);
        if (source) {
            const columnFormats = new Map();
            source.forEach((col) => {
                if (col.customFormat)
                    columnFormats.set(col.name, col.customFormat);
            });
            const result = iConfigHelper.evalSql(ToolConfig.getStringValue(iConfigHelper.getParameters(), this.formula), FormulaConfig_1.PRESTO_ID_CHAR, FormulaConfig_1.STRING_CHAR, true, source, columnFormats);
            const newColumnFormats = new Map();
            if (result.type !== null) {
                const dependents = [];
                result.colArgs.forEach((columnName) => {
                    dependents.push(exp.findSourceColumn(this.in, columnName));
                });
                exp
                    .copyAll(this.in)
                    .makeNew(new Column(ToolConfig.getStringValue(iConfigHelper.getParameters(), this.column), result.type), dependents, true);
                if (result.format) {
                    newColumnFormats.set(ToolConfig.getStringValue(iConfigHelper.getParameters(), this.column), result.format.copyWithAutoGeneratedTrue());
                }
            }
            exp.initializeColumnFormats().setColumnFormats(newColumnFormats).setColumnFormats(this.customFormats);
        }
        return tsb.build();
    }
};
FormulaConfig.PRESTO_ID_CHAR = '"';
FormulaConfig.SPARK_ID_CHAR = '`';
FormulaConfig.STRING_CHAR = "'";
FormulaConfig = FormulaConfig_1 = __decorate([
    JsonSchema(schema),
    JsonDiscriminatorValue(ToolType.FORMULA)
], FormulaConfig);
export { FormulaConfig };
//# sourceMappingURL=formulaConfig.js.map